import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy, Input, signal, Output, EventEmitter, inject, DestroyRef, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CampaignWithCountDto } from '../../../../common/general-types/campaigns.dto';
import { BriefApplicationResponseDto, BriefResponseDto } from '../../../../common/general-types/briefs.dto';
import { CampaignService } from '../../../../services/campaign/campaign.service';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { take } from 'rxjs';
import { ImageService } from '../../../../services/image/image.service';
import { BriefService } from '../../../../services/brief/brief.service';
import { BriefApplicationService } from '../../../../services/brief-application/brief-application.service';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { Router } from '@angular/router';
import { ManageBriefTabsService } from '../../../manage-brief-tabs/service/manage-brief-tabs/manage-brief-tabs.service';
import { PromoteToBriefCache } from './promote-to-brief.cache';

@Component({
  selector: 'app-promote-to-brief',
  standalone: true,
  imports: [],
  templateUrl: './promote-to-brief.component.html',
  styleUrl: './promote-to-brief.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class PromoteToBriefComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('drawerDialog', { static: false }) drawerDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('openDrawerButton', { static: false }) openDrawerButton?: ElementRef<HTMLButtonElement>;

  @Input() currentBriefId: string | undefined;
  @Output() close = new EventEmitter<void>();

  private dialogCloseHandler?: () => void;

  allCampaigns = signal<CampaignWithCountDto[] | undefined>(undefined);
  loadingCampaigns = signal<boolean>(false);
  campaignsError = signal<boolean>(false);

  selectedBrief = signal<BriefResponseDto | undefined>(undefined);

  runningPromote = signal<boolean>(false);

  selectedCampaign = signal<CampaignWithCountDto | undefined>(undefined);
  /** Every brief on the selected campaign, as loaded. */
  private campaignBriefs = signal<BriefResponseDto[] | undefined>(undefined);
  /** The briefs creators can be promoted into: the campaign's briefs minus the one they're on now. */
  linkedBriefs = computed(() =>
    this.campaignBriefs()?.filter((brief) => brief.id !== this.sourceBriefId()),
  );
  loadingBriefs = signal<boolean>(false);
  briefsError = signal<boolean>(false);

  /** Set once the user picks a campaign, so a late campaign list doesn't override their choice. */
  private userPickedCampaign = false;
  private sourceBriefId = signal<string | undefined>(undefined);


  /** Services */
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private currentUserService = inject(CurrentUserService);
  private campaignService = inject(CampaignService);
  private manageBriefTabsService = inject(ManageBriefTabsService);
  private cache = inject(PromoteToBriefCache);
  public imageService = inject(ImageService);
  public briefService = inject(BriefService);
  public alertService = inject(AlertService);
  private briefApplicationService = inject(BriefApplicationService);
  /** Services - END */



  ngOnInit(): void {
    const currentBrief = this.manageBriefTabsService.focussedBrief();
    this.sourceBriefId.set(this.currentBriefId ?? currentBrief?.id);

    // Start both requests together: the creators are usually promoted into another brief on the
    // same campaign, so its briefs can load while the campaign list is still on its way.
    const currentCampaignId = currentBrief?.campaignId;
    this.getAllCampaigns(currentCampaignId);
    if (currentCampaignId) {
      this.loadBriefs(currentCampaignId);
    }
  }

  getAllCampaigns(preselectCampaignId?: string) {
    const brandId: string | undefined = this.currentUserService.focussedBrand?.id;
    if (!brandId) {
      return;
    }

    const cached = this.cache.campaigns(brandId);
    if (cached) {
      this.applyCampaigns(cached, preselectCampaignId);
    } else {
      this.loadingCampaigns.set(true);
    }
    this.campaignsError.set(false);

    this.campaignService.getAllBusinessCampaigns(brandId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const campaigns = response as CampaignWithCountDto[];
          this.cache.setCampaigns(brandId, campaigns);
          this.applyCampaigns(campaigns, preselectCampaignId);
          this.loadingCampaigns.set(false);
        },
        error: (err: unknown) => {
          console.error('Error fetching campaigns:', err);
          this.loadingCampaigns.set(false);
          // Keep showing a cached list if we have one; only flag the error when there is nothing.
          this.campaignsError.set(!this.allCampaigns());
        },
      });
  }

  private applyCampaigns(campaigns: CampaignWithCountDto[], preselectCampaignId?: string) {
    this.allCampaigns.set(campaigns);
    if (this.userPickedCampaign) {
      // Swap in the fresh object for the user's pick, if it's still there.
      const picked = this.selectedCampaign();
      if (picked) {
        this.selectedCampaign.set(campaigns.find((c) => c.id === picked.id) ?? picked);
      }
      return;
    }
    if (preselectCampaignId) {
      const match = campaigns.find((c) => c.id === preselectCampaignId);
      if (match) {
        this.selectedCampaign.set(match);
      }
    }
  }

  selectCampaign(campaign: CampaignWithCountDto | undefined) {
    this.userPickedCampaign = true;
    if (campaign?.id === this.selectedCampaign()?.id) {
      return;
    }
    this.selectedCampaign.set(campaign);
    this.selectedBrief.set(undefined);
    this.campaignBriefs.set(undefined);
    this.getLinkedBriefs();
  }

  getLinkedBriefs() {
    const campaign = this.selectedCampaign();
    if (campaign) {
      this.loadBriefs(campaign.id);
    } else {
      this.loadingBriefs.set(false);
      this.briefsError.set(false);
    }
  }

  private loadBriefs(campaignId: string) {
    const cached = this.cache.briefs(campaignId);
    if (cached) {
      this.campaignBriefs.set(cached);
    } else {
      this.loadingBriefs.set(true);
    }
    this.briefsError.set(false);

    // Lightweight list endpoint: the drawer only needs titles, not each brief's application stats.
    this.campaignService.getCampaignBriefs(campaignId)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (briefs) => {
          this.cache.setBriefs(campaignId, briefs);
          // Ignore the answer if the user has since moved to another campaign.
          if (this.activeCampaignId() !== campaignId) {
            return;
          }
          this.campaignBriefs.set(briefs);
          this.loadingBriefs.set(false);
        },
        error: (err: unknown) => {
          console.error('Error fetching linked briefs:', err);
          if (this.activeCampaignId() !== campaignId) {
            return;
          }
          this.loadingBriefs.set(false);
          this.briefsError.set(!this.campaignBriefs());
        },
      });
  }

  /** The campaign whose briefs we're showing: the user's pick, or the current brief's campaign until they pick. */
  private activeCampaignId(): string | undefined {
    if (this.userPickedCampaign) {
      return this.selectedCampaign()?.id;
    }
    return this.selectedCampaign()?.id ?? this.manageBriefTabsService.focussedBrief()?.campaignId;
  }

  selectBrief(brief: BriefResponseDto | undefined) {
    this.selectedBrief.set(brief);
  }

  removeCreator(creator: any) {
    this.briefService.creatorsToPromote.set(this.briefService.creatorsToPromote().filter((c: BriefApplicationResponseDto) => c.id !== creator.id));
  }

  promoteCreators() {
    console.log('promoteCreators(): ', this.briefService.creatorsToPromote());
    this.runningPromote.set(true);

    if (this.selectedCampaign() && this.selectedBrief() && this.briefService.creatorsToPromote().length > 0) {
      const creators = this.briefService.creatorsToPromote().map((c: BriefApplicationResponseDto) => c.creator?.blazeFirebaseUid);
      const briefId = this.selectedBrief()?.id;
      const campaignId = this.selectedCampaign()?.id;

      const payload = {
        applications: this.getApplicationIds(),
        creators: creators,
        toBrief: briefId,
        fromBrief: this.sourceBriefId(),
        campaignId: campaignId
      }

      this.briefApplicationService.promoteCreators(payload).pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response) => {
          this.runningPromote.set(false);
          void this.promotionSuccess(response as { completed: number, failed: number, total: number }, briefId!);
        },
        error: (err: unknown) => {
          console.error('Error promoting creators:', err);
          this.runningPromote.set(false);
          const apiMessage = (err as { error?: { message?: unknown } } | null)?.error?.message;
          void this.alertService.confirm({
            title: 'Promotion failed',
            message: typeof apiMessage === 'string' ? apiMessage : 'We couldn’t promote these creators. Try again in a moment.',
            confirmText: 'OK',
            cancelText: 'Close',
            type: 'alert',
            icon: 'error',
          });
        },
      });
    } else {
      this.runningPromote.set(false);
    }
  }

  getApplicationIds(): { creatorId: string, applicationId: string, hiredPricing: any }[] {
    return this.briefService.creatorsToPromote().map((c: BriefApplicationResponseDto) => ({
      creatorId: c.creator?.blazeFirebaseUid ?? '',
      applicationId: c.id ?? '',
      hiredPricing: c.hiredBriefPricing ?? undefined
    }));
  }

  async promotionSuccess(promotedRes: { completed: number, failed: number, total: number }, successBriefId: string) {
    this.briefService.creatorsToPromote.set([]);
    const confirm = await this.alertService.confirm({
      title: 'Woohoo! 🥳',
      message: `${promotedRes.completed} creators promoted to brief, ${promotedRes.failed} failed.`,
      confirmText:'Go To Brief',
      cancelText: 'Close',
      type: 'confirm',
      icon: 'info'
    });

    if (confirm) {
      this.closeDrawer();
      this.router.navigate([`/manage-brief/${successBriefId}`]);
    } else {
      this.closeDrawer();
    }
  }


  ngAfterViewInit(): void {
    if (this.openDrawerButton?.nativeElement) {
      setTimeout(() => {
        this.openDrawerButton?.nativeElement.click();
      }, 0);
    }

    if (this.drawerDialog?.nativeElement) {
      this.dialogCloseHandler = () => {
        this.closeDrawer();
      };
      this.drawerDialog.nativeElement.addEventListener('close', this.dialogCloseHandler);
    }
  }

  closeDrawer() {
    this.close.emit();
  }

  ngOnDestroy(): void {
    if (this.drawerDialog?.nativeElement && this.dialogCloseHandler) {
      this.drawerDialog.nativeElement.removeEventListener('close', this.dialogCloseHandler);
    }
  }
}
